import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLogin } from "@api/hooks";
import { showSuccess, showError, FormLayout, FormCard, FormField, Button } from "@common";
import { FORM_IDS, getDefaultRouteForUser, loginSchema } from "@utils";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";

export default function Login() {
  const nav = useNavigate();
  const loginMutation = useLogin();

  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(FORM_IDS.AUTH_LOGIN);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(values) {
    try {
      const data = await loginMutation.mutateAsync(values);

      recordSubmitSuccess();

      // Show success toast
      showSuccess(`Welcome back, ${data?.user?.full_name || "user"}!`);

      // Navigate based on role
      const dest = getDefaultRouteForUser(data?.user);
      nav(dest, { replace: true });
    } catch (error) {
      // Show error toast
      showError("Incorrect email or password. Please try again.");
    }
  }

  return (
    <FormLayout>
      <FormCard title="Sign In" onSubmit={handleSubmit(onSubmit)} {...formTrackingProps}>
        <FormField
          label="Email"
          name="email"
          type="email"
          placeholder="Enter your email"
          register={register}
          error={errors.email?.message}
        />

        <FormField
          label="Password"
          name="password"
          type="password"
          placeholder="Min. 6 characters"
          register={register}
          error={errors.password?.message}
        />

        <div className="mb-2 text-right">
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => nav("/forgot-password")}
            className="text-sm text-smiles-blue hover:underline"
          >
            Forgot password?
          </Button>
        </div>

        <Button as="button" type="submit" className="w-full" disabled={!isValid || isSubmitting}>
          {isSubmitting ? "Signing in…" : "Sign In"}
        </Button>

        <div className="mt-4 text-center">
          <span className="text-smiles-lightGray">Don't have an account? </span>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => nav("/register")}
            className="text-smiles-blue font-medium hover:underline"
          >
            Create Account
          </Button>
        </div>

        {/* <div className="mt-3 text-center">
          <a
            className="inline-block text-palette-indigo-badge font-medium hover:text-palette-indigo-heading"
            href="https://youtube.com/watch?si=7YpOems5HIJLt9mI&v=SM-VtUP88IY&feature=youtu.be"
            target="_blank"
            rel="noreferrer"
          >
            Link to Smiles Analytics tutorial video
          </a>
        </div> */}
      </FormCard>
    </FormLayout>
  );
}
